import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Camera, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import axios from "axios";

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [isPrewarming, setIsPrewarming] = useState(true);

  useEffect(() => {
    if (user) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  // Prewarm the backend on page load to prevent cold-start 520s on OAuth
  useEffect(() => {
    const prewarm = async () => {
      try {
        // Hit health endpoint to warm up the backend
        await axios.get(`${API}/health`, { timeout: 10000 });
        // Also warm up OAuth endpoint specifically
        await axios.get(`${API}/auth/google/health`, { timeout: 10000 });
      } catch (error) {
        // Ignore errors - this is just for warming
        console.log("[Prewarm] Backend warming complete or unavailable");
      } finally {
        setIsPrewarming(false);
      }
    };
    prewarm();
  }, []);

  // Handle OAuth errors
  useEffect(() => {
    const error = searchParams.get("error");
    const message = searchParams.get("message");
    
    if (error) {
      if (error === "account_suspended") {
        toast.error("Your account has been suspended. Please contact support.");
      } else if (error === "oauth_failed") {
        toast.error(`Login failed: ${message || "Please try again"}`);
      }
    }
  }, [searchParams]);

  const handleGoogleLogin = () => {
    // Use custom Google OAuth endpoint
    window.location.href = `${API}/auth/google/login`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Camera className="w-12 h-12 text-primary" />
          </div>
          <h1 className="text-4xl tracking-tight mb-2">360Lumina</h1>
          <p className="text-muted-foreground">Photographer Dashboard</p>
        </div>

        <div className="bg-card border border-border rounded-sm p-8">
          <h2 className="text-2xl text-center mb-6">Welcome Back</h2>
          
          {/* Show loading indicator during backend prewarm */}
          {isPrewarming && (
            <div className="text-center mb-4 text-muted-foreground text-sm flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Connecting...</span>
            </div>
          )}
          
          <Button
            onClick={handleGoogleLogin}
            disabled={isPrewarming}
            className="w-full btn-primary rounded-sm flex items-center justify-center gap-3"
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </Button>

          <p className="text-center text-sm text-muted-foreground mt-6">
            By continuing, you agree to our Terms of Service and Privacy Policy.
          </p>
        </div>

        <p className="text-center text-sm text-muted-foreground mt-8">
          <a href="/" className="text-primary hover:underline">
            Back to 360Lumina
          </a>
        </p>
        <p className="text-center text-sm text-muted-foreground mt-2">
          <a href="/admin/login" className="text-muted-foreground hover:text-primary hover:underline">
            Admin Login
          </a>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
